import React, { useState } from 'react';

import { FullScreenModal } from '@opendoor/bricks/complex/Modal';
import { Button, Card, Flex, Link, Text } from '@opendoor/bricks/core';

import { useAcceptListingLead } from '../../graphql/athena/mutations/AcceptListingLead';
import { useRejectListingLead } from '../../graphql/athena/mutations/RejectListingLead';
import { getReadableAddressFromSingleLead, SingleLead } from '../../helpers/getAllLeadData';
import { LeadDetailsPage } from './LeadDetailsPage';
import { getDisplayStateFromSingleLead } from './utils';

/**
 * Renders the summary card for an Agent's lead
 *
 * storybook-check-ignore
 */
interface LeadCardProps {
  name?: string;
  address?: string;
  expiresAt?: string;
  lwcProductId?: string;
  token: string;
  tokenToShowDetailsFor?: string;
  agentCustomerUuid: string;
  agentLeadId: string;
  singleLead: SingleLead;
  isALALead?: boolean;
}

/** Card that displays lead information for an agent*/
export const LeadCard: React.FC<LeadCardProps> = ({
  name,
  expiresAt,
  agentCustomerUuid,
  agentLeadId,
  singleLead,
  isALALead,
}) => {
  const handleClose = () => {
    setOpen(false);
  };

  const displayState = getDisplayStateFromSingleLead(singleLead);
  const pendingAssignmentAcceptance = singleLead.lwcProductData.pendingAssignmentAcceptance;
  const fullAddress = getReadableAddressFromSingleLead(singleLead);
  const { acceptLead } = useAcceptListingLead();
  const { rejectLead } = useRejectListingLead();
  const [isOpen, setOpen] = useState(false);
  const { isLatestLwcLead } = singleLead.lwcProductData;

  // If this is not the latest LWC lead, don't show it
  // potentially we could show it but greyed out,
  // i.e. a tombstone for the lead
  if (!isLatestLwcLead) {
    return null;
  }

  return (
    <Card
      variant="secondary"
      display="flex"
      flexDir="column"
      boxShadow="unset"
      border="1px solid"
      borderColor="neutrals40"
      justifyContent={['center', 'center', 'center']}
      alignItems="space-between"
    >
      {isALALead && (
        <span
          style={{
            backgroundColor: 'red',
            color: 'white',
            borderRadius: '5px',
            padding: '5px',
            width: 'fit-content',
          }}
        >
          ALA
        </span>
      )}
      {/* Customer data */}
      <LeadInfo fieldName="Customer" fieldValue={name} />
      <LeadInfo fieldName="Address" fieldValue={fullAddress} lineBreak={true} />
      <LeadInfo fieldName="Status" fieldValue={displayState} />
      <LeadInfo fieldName="Offer Expires" fieldValue={expiresAt} />

      <Flex gridGap="3" my="3" flexDirection="column">
        {pendingAssignmentAcceptance && (
          <Flex gridGap="inherit">
            <Button
              analyticsName="accept-lead"
              aria-label=""
              width="100%"
              type="button"
              onClick={() => acceptLead(agentCustomerUuid, agentLeadId)}
              backgroundColor={'green50'}
              sx={{
                ':hover': {
                  backgroundColor: 'lightgrey',
                  color: 'green50',
                },
              }}
            >
              Accept
            </Button>
            <Button
              type="button"
              onClick={() => rejectLead(agentCustomerUuid, agentLeadId)}
              analyticsName="reject-lead"
              aria-label=""
              width="100%"
              backgroundColor={'red50'}
              sx={{
                ':hover': {
                  backgroundColor: 'lightgrey',
                  color: 'red50',
                },
              }}
            >
              Reject
            </Button>
          </Flex>
        )}
        <Link aria-label="" analyticsName="lwc-details" onClick={() => setOpen(true)} width="100%">
          Show details
        </Link>

        <FullScreenModal
          isOpen={isOpen}
          analyticsName="lwc-details-modal"
          title={'Lead Details'}
          onRequestClose={handleClose}
        >
          <LeadDetailsPage leadDetails={singleLead} brokerageView={false} />
        </FullScreenModal>
      </Flex>
    </Card>
  );
};

interface LeadInfoProps {
  fieldName?: string;
  fieldValue?: string;
  lineBreak?: boolean;
}

const LeadInfo: React.FC<LeadInfoProps> = ({ fieldName, fieldValue, lineBreak }) => {
  return (
    <Flex width="100%" justifyContent="space-between" flexDirection={lineBreak ? 'column' : 'row'}>
      <Text>{fieldName}: </Text>
      <Text>{fieldValue}</Text>
    </Flex>
  );
};
